import React, { useState } from 'react';
import {
  Check,
  CircleNotch,
  Copy,
  FileCode,
  Globe,
  Link as LinkIcon,
  ShareNetwork,
  X
} from '@phosphor-icons/react';
import { BACKEND_URL } from '../config';

const SITE_URL = window.location.origin;

export const ShareButton = ({ slug, visibility, showExport = false, className = '' }) => {
  const [open, setOpen] = useState(false);

  return (
    <div className="relative">
      <button
        data-testid="share-automation-btn"
        onClick={() => setOpen(!open)}
        className={`flex items-center gap-2 text-sm text-[#8b8fa3] hover:text-white transition-colors px-3 py-1.5 rounded-lg border border-[rgba(255,255,255,0.08)] hover:border-[rgba(255,255,255,0.15)] ${className}`}
      >
        <ShareNetwork size={16} />
        Share
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <ShareDropdown
            slug={slug}
            visibility={visibility}
            showExport={showExport}
            onClose={() => setOpen(false)}
          />
        </>
      )}
    </div>
  );
};

const ShareDropdown = ({ slug, visibility, showExport = false, onClose }) => {
  const [copiedField, setCopiedField] = useState(null);
  const [exporting, setExporting] = useState(false);

  const installLink = `${SITE_URL}/install/${slug}`;
  const publicLink = `${SITE_URL}/automations/${slug}`;

  const handleCopy = async (text, field) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (e) {
      const ta = document.createElement('textarea');
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    }
  };

  const handleExportBlueprint = async () => {
    try {
      setExporting(true);
      const res = await fetch(`${BACKEND_URL}/api/public/automations/${slug}/blueprint`);
      if (!res.ok) throw new Error('Export failed');
      const data = await res.json();
      const blob = new Blob([JSON.stringify(data.blueprint, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${slug}-blueprint.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      setCopiedField('blueprint');
      setTimeout(() => setCopiedField(null), 2000);
    } catch (err) {
      console.error('Blueprint export failed:', err);
    } finally {
      setExporting(false);
    }
  };

  return (
    <div
      data-testid="share-dropdown"
      className="absolute right-0 top-full mt-2 z-50 w-72 rounded-xl border border-[rgba(255,255,255,0.08)] bg-[#16181c] shadow-xl"
    >
      <div className="p-3 border-b border-[rgba(255,255,255,0.08)]">
        <div className="flex items-center justify-between">
          <h4 className="text-white text-xs font-semibold">Share Automation</h4>
          <button onClick={onClose} className="text-[#555] hover:text-white transition-colors">
            <X size={14} />
          </button>
        </div>
      </div>

      <div className="p-2 space-y-1">
        {/* Copy Install Link */}
        <button
          data-testid="copy-install-link-btn"
          onClick={() => handleCopy(installLink, 'install')}
          className="w-full flex items-center gap-3 p-2.5 rounded-lg hover:bg-[rgba(255,255,255,0.04)] transition-colors text-left"
        >
          <div className="w-8 h-8 rounded-lg bg-[rgba(99,102,241,0.12)] flex items-center justify-center shrink-0">
            <LinkIcon size={14} className="text-[#6366F1]" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-white text-xs font-medium">Copy Install Link</p>
            <p className="text-[#555] text-[10px] truncate">{installLink}</p>
          </div>
          {copiedField === 'install' ? (
            <Check size={14} className="text-green-400 shrink-0" />
          ) : (
            <Copy size={14} className="text-[#555] shrink-0" />
          )}
        </button>

        {/* Copy Public Page Link */}
        {visibility !== 'internal_only' && (
          <button
            data-testid="copy-public-link-btn"
            onClick={() => handleCopy(publicLink, 'public')}
            className="w-full flex items-center gap-3 p-2.5 rounded-lg hover:bg-[rgba(255,255,255,0.04)] transition-colors text-left"
          >
            <div className="w-8 h-8 rounded-lg bg-[rgba(236,72,153,0.12)] flex items-center justify-center shrink-0">
              <Globe size={14} className="text-[#EC4899]" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-white text-xs font-medium">Copy Public Page Link</p>
              <p className="text-[#555] text-[10px] truncate">{publicLink}</p>
            </div>
            {copiedField === 'public' ? (
              <Check size={14} className="text-green-400 shrink-0" />
            ) : (
              <Copy size={14} className="text-[#555] shrink-0" />
            )}
          </button>
        )}

        {/* Export Blueprint — only for authenticated/internal contexts */}
        {showExport && (
        <button
          data-testid="export-blueprint-btn"
          onClick={handleExportBlueprint}
          disabled={exporting}
          className="w-full flex items-center gap-3 p-2.5 rounded-lg hover:bg-[rgba(255,255,255,0.04)] transition-colors text-left disabled:opacity-50"
        >
          <div className="w-8 h-8 rounded-lg bg-[rgba(16,185,129,0.12)] flex items-center justify-center shrink-0">
            {exporting ? (
              <CircleNotch size={14} className="text-[#10B981] animate-spin" />
            ) : (
              <FileCode size={14} className="text-[#10B981]" />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-white text-xs font-medium">Export Blueprint</p>
            <p className="text-[#555] text-[10px]">Download as JSON file</p>
          </div>
          {copiedField === 'blueprint' ? (
            <Check size={14} className="text-green-400 shrink-0" />
          ) : (
            <FileCode size={14} className="text-[#555] shrink-0" />
          )}
        </button>
        )}
      </div>
    </div>
  );
};

export default ShareButton;
